import 'swiper/css'
import 'swiper/css/navigation'

import { MapDirectionsRenderer } from '@c/Catalog'
import { circleOptions, mapOptions } from '@c/Catalog/Map/mapStyler'
import { createMarkerIcon } from '@c/Catalog/Map/utils/createMarkerIcon'
import TourInfoBox from '@c/Landing/TourInfoBox'
import { IconClock, IconExclusive, IconThematic } from '@c/Ui/Icons'
import { IconPlusBig } from '@c/Ui/Icons/IconPlusBig'
import { IconRoute } from '@c/Ui/Icons/IconRoute'
import { IconStarRating } from '@c/Ui/Icons/IconStartRating'
import { faRoute } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GoogleMap, Marker, TransitLayer } from '@react-google-maps/api'
import { type MutableRefObject, type SetStateAction, useState } from 'react'
import ReactGA from 'react-ga4'
import type { Swiper as SwiperType } from 'swiper'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'

import type { CoordinatesAndHeading, Gallery, MapPoint } from '@/core/interface'
import type { Excursion, TExcursionPoint } from '@/core/interface/Excursion'
import { formatPriceWithTwoDecimals } from '@/core/utils/numbers'

type Props = {
  excursions: Excursion[]
  activeCategory: string
  initialExcursionRef: MutableRefObject<{ [p: string]: HTMLDivElement | null }>
  currentSlideIndices: { [key: string]: number }
  setCurrentSlideIndices: (value: SetStateAction<{ [p: string]: number }>) => void
  mapCenter: { lat: number; lng: number }
  currentZoom: number
  onLoad: (map: google.maps.Map) => void
  onUnmount: (_map: google.maps.Map | null) => void
  onZoomChanged: () => void
  onDragEnd: () => void
  getMemoizedMarkerList: (excursion: Excursion, mode: 'preview') => any
  map: google.maps.Map | null
  userPosition: CoordinatesAndHeading
  payedTours: string[]
  isPayedClient: boolean
  setLastViewRoute: (value: SetStateAction<Excursion | null>) => void
  setViewRoute: (value: SetStateAction<Excursion | null>) => void
  adjustCoordinates: (points: TExcursionPoint[]) => (
    | {
      coordinate: { latitude: number; longitude: number }
      rank: number
      description: string
      step: number
      id: string
      type: string
      title: string
      audioURI: string
      gallery: Gallery[]
      tags: string[]
    }
    | TExcursionPoint
  )[]
  setPlaces: (value: SetStateAction<MapPoint[]>) => void
}
export function FilteredRoutesList(props: Props) {
  const {
    excursions,
    isPayedClient,
    setPlaces,
    adjustCoordinates,
    setViewRoute,
    setLastViewRoute,
    payedTours,
    mapCenter,
    userPosition,
    map,
    getMemoizedMarkerList,
    onDragEnd,
    onZoomChanged,
    onLoad,
    onUnmount,
    currentZoom,
    activeCategory,
    setCurrentSlideIndices,
    currentSlideIndices,
    initialExcursionRef,
  } = props

  const [excursionSwiper, setExcursionSwiper] = useState<{ [key: string]: SwiperClass }>({})
  const [buyLinkLoading, setBuyLinkLoading] = useState(false)
  const [descriptionToggle, setDescriptionToggle] = useState('excursion_id')

  function formatOldPrice(price: number): string {
    return formatPriceWithTwoDecimals(price)
  }

  function showHideDescriptionText(excursion_id = '', text = '', charMax = 100) {
    if (descriptionToggle == excursion_id) return text
    return text.substring(0, charMax) + '...'
  }

  function filterExcursionsByCategory(excursion: Excursion): boolean {
    if (activeCategory === 'all') {
      return true
    }
    if (activeCategory === 'my' && payedTours.includes(excursion.id)) {
      return true
    }
    return excursion.category === activeCategory
  }


  const filteredExcursions = excursions.filter(filterExcursionsByCategory)

  const sortedExcursions = [...filteredExcursions].sort((a, b) => {
    const orderA = a?.order ?? 0
    const orderB = b?.order ?? 0
    return orderA - orderB
  })

  return (
    <div className="routes">
      {sortedExcursions.map((excursion) => (
        <div
          className="routes__item"
          key={excursion.id}
          ref={(el) => {
            initialExcursionRef.current[excursion.id] = el
          }}
        >
          <p className="routes__item-title">{excursion?.subtitle ? `${excursion.title} \u2014 ${excursion.subtitle}` : excursion?.title}</p>
          <IconStarRating
            rating={excursion?.rating || 0}
          ></IconStarRating>
          <TourInfoBox
            time={excursion?.duration || '0'}
            distance={excursion?.distance?.toString() || '0'}
            price={excursion?.price || '0'}
            discount={excursion?.discount}
            old_price={excursion.old_price}
          ></TourInfoBox>
          <br />
          <p
            className="tour_description"
            dangerouslySetInnerHTML={{
              __html: showHideDescriptionText(excursion.id, excursion.tourDescription),
            }}
          ></p>
          {descriptionToggle != excursion.id && (
            <a
              className="description_button"
              onClick={() => {
                setDescriptionToggle(excursion.id)
              }}
            >
              more
            </a>
          )}
          <div
            className="view-route-container"
            onClick={() => {
              excursionSwiper[excursion.id].slideTo(excursion.landmarks.length, 0)
              const element = document.getElementById(excursion.id)
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
              }
            }}
          >
            <div className="view-route-icon start-icon">
              <IconRoute />
            </div>
            <div className="view-route-content">
              <p>View Route</p>
            </div>
            <div className="view-route-icon end-icon">
              <IconPlusBig />
            </div>
          </div>
          <div className="routes__item-dotsNames">
            {(() => {
              const filteredPoints = excursion.landmarks.filter((item) => item.title)
              const totalItems = filteredPoints.length + 1 // +1 for "View Route"
              const halfPoint = Math.ceil(totalItems / 2)

              return (
                <>
                  <div className="routes__item-column">
                    {filteredPoints.slice(0, halfPoint).map((point, index: number) => (
                      <p
                        className="routes__item-dot-title"
                        key={point.id}
                        onClick={() => excursionSwiper[excursion.id].slideTo(index, 0)}
                        style={{
                          fontWeight:
                            index === (currentSlideIndices[excursion.id] || 0) ? 'bold' : 'normal',
                        }}
                      >
                        <span className="number">{index + 1}.</span> {point.title}
                      </p>
                    ))}
                  </div>
                  <div className="routes__item-column">
                    {[
                      ...filteredPoints.slice(halfPoint),
                      { id: 'view-route', title: 'View Route' },
                    ].map((point, index: number) => (
                      <p
                        className="routes__item-dot-title"
                        key={point.id}
                        onClick={() =>
                          excursionSwiper?.[excursion.id]?.slideTo(index + halfPoint, 0)
                        }
                        style={{
                          fontWeight:
                            index + halfPoint === (currentSlideIndices[excursion.id] || 0)
                              ? 'bold'
                              : 'normal',
                        }}
                      >
                        {/* <span className="dot">•</span> */}
                        {point.id !== 'view-route' ? (
                          <span className="number">{index + halfPoint + 1}.</span>
                        ) : (
                          <span className="number">
                            <FontAwesomeIcon icon={faRoute} />
                          </span>
                        )}{' '}
                        {point.title}
                      </p>
                    ))}
                  </div>
                </>
              )
            })()}
          </div>
          <div className="swiper-container route-swiper" id={excursion.id}>
            <div className="tour-tag">
              {excursion.category === 'exclusive' && (
                <div className="tour-tag-container">
                  <IconExclusive></IconExclusive> Exclusive
                </div>
              )}
              {excursion.category === 'highlights' && (
                <div className="tour-tag-container">
                  <IconClock></IconClock> Highlights
                </div>
              )}{' '}
              {excursion.category === 'thematic' && (
                <div className="tour-tag-container">
                  <IconThematic></IconThematic> Thematic
                </div>
              )}
            </div>
            <Swiper
              onInit={(swiper: SwiperClass) => {
                setExcursionSwiper((prev) => ({
                  ...prev,
                  [excursion.id]: swiper,
                }))
              }}
              spaceBetween={50}
              slidesPerView={1}
              height={261}
              modules={[Navigation]}
              nested={true}
              touchStartPreventDefault={false}
              navigation={{
                prevEl: `.swiper-button-prev-${excursion.id}`,
                nextEl: `.swiper-button-next-${excursion.id}`,
              }}
              // loop
              onActiveIndexChange={(swiper: SwiperType) => {
                setCurrentSlideIndices((prev) => ({
                  ...prev,
                  [excursion.id]: swiper.activeIndex,
                }))
                ReactGA.event({
                  category: 'excursion',
                  action: 'WA_SwipeItem',
                  label: `${excursion.id}_${swiper.activeIndex}`,
                })
              }}
            >
              {excursion.landmarks.map((point) => (
                <SwiperSlide className={'slide'} key={point.id}>
                  <img
                    className={`routes__item-image ${excursion?.discount ? 'routes__item-image-special' : ''}`}
                    src={point.gallery?.[0]?.uri}
                    alt={point.title}
                  />
                  {point.title ? (
                    <p className="routes__item__gallery-title">{point.title}</p>
                  ) : null}
                  {excursion?.discount ? (
                    <div className="routes__item-special-offer">
                      <h5 className="routes__item-special-offer-title">DEAL OF<br />THE MONTH</h5>
                      <p className="routes__item-subtitle">
                        <span className="routes__item-subtitle-text">for just</span>
                        <span className="routes__item-subtitle-text old-price">{excursion?.old_price ? formatOldPrice(excursion.old_price) : ''}€</span>
                        <span className="routes__item-subtitle-text new-price">{excursion?.price ? formatPriceWithTwoDecimals(excursion.price) : ''}€</span>
                      </p>
                    </div>
                  ) : null}
                </SwiperSlide>
              ))}
              {excursion?.landmarks.length ? (
                <SwiperSlide key={excursion.id}>
                  <GoogleMap
                    mapContainerStyle={{
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    center={mapCenter}
                    zoom={currentZoom}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onZoomChanged={onZoomChanged}
                    onDragEnd={onDragEnd}
                    options={mapOptions}
                  >
                    {excursion.landmarks.length > 0 && (
                      <MapDirectionsRenderer places={excursion.landmarks} travelMode="WALKING" />
                    )}
                    <TransitLayer />
                    {getMemoizedMarkerList(excursion, 'preview').map(
                      ({ key, icon, clickable, onClick, position }, index) => (
                        <Marker
                          icon={icon}
                          key={key}
                          clickable={clickable}
                          onClick={onClick}
                          position={position}
                          zIndex={1000 - index}
                        />
                      ),
                    )}
                    <Marker
                      key={80000000}
                      options={circleOptions}
                      icon={createMarkerIcon({
                        window: window,
                        idx: null,
                        iconUsing: 'route',
                        id: null,
                        rank: 1,
                        isHighlighted: false,
                        selectedPoint: '',
                        category: 'user-current-location',
                        zoom: map?.getZoom(),
                        heading: userPosition.heading,
                      })}
                      position={{ lat: userPosition.latitude, lng: userPosition.longitude }}
                    />
                  </GoogleMap>
                </SwiperSlide>
              ) : null}
              <div className={`swiper-button-prev-${excursion.id}`}></div>
              <div className={`swiper-button-next-${excursion.id}`}></div>
            </Swiper>
          </div>
          {(isPayedClient || payedTours.includes(excursion.id)) && (
            <div className="i-choose-this">
              <button
                className="i-choose-this-button"
                onClick={() => {
                  setLastViewRoute(excursion)
                  setViewRoute(excursion)
                  const adjustedPoints = adjustCoordinates(excursion.landmarks)
                  // TODO maybe error | need test
                  setPlaces(adjustedPoints)
                  ReactGA.event({
                    category: 'excursion',
                    action: 'WA_ExcursionClick',
                    label: excursion.id,
                  })
                }}
                id={`tour-go-${excursion.id}`}
              >
                Go to your tour &gt;
              </button>
            </div>
          )}
          {!isPayedClient && !payedTours.includes(excursion.id) && (
            <div className="special-buttons">
              {!buyLinkLoading && (
                <a
                  className="special-offer-button"
                  onClick={() => buyTourGenerateLink(excursion.id, setBuyLinkLoading)}
                  id={`tour-${excursion.id}`}
                >
                  Buy Tour &gt;
                </a>
              )}
              {buyLinkLoading && (
                <button className="special-offer-button" disabled>
                  Buy Tour <div className="loader-buy-tour"></div>
                </button>
              )}
            </div>
          )}
          <a
            className="view__route__more"
            href={`${process.env.REACT_APP_WP_URL}/tours/${excursion.id}/`}
            id={`tour-more-${excursion.id}`}
          >
            More about this tour &gt;
          </a>
        </div>
      ))}
    </div>
  )
}

function getCookie(name: string) {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=')
    if (cookieName === name) {
      return decodeURIComponent(cookieValue)
    }
  }
  return null
}

async function buyTourGenerateLink(tour_id: string, setBuyLinkLoading: (bool: boolean) => void) {
  setBuyLinkLoading(true)
  const unsuccessUrl = `${process.env.REACT_APP_WP_URL}/my-account/?referrer_tour=${tour_id}`
  try {
    const refresh_token = getCookie('WM_RefreshToken')
    const user_id = getCookie('WM_ID')

    const response = await fetch(
      `${process.env.REACT_APP_WP_URL}/wp-json/wm-subscription/v1/tours/new_from_webapp`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add other headers if needed (like authorization)
        },
        body: JSON.stringify({
          RefreshToken: refresh_token,
          ID: user_id,
          tourid: tour_id,
          token: process.env.REACT_APP_WP_TOKEN,
        }),
      },
    )

    if (!response.ok) {
      // throw new Error(`HTTP error! status: ${response.status}`)
      window.location.href = unsuccessUrl
    }

    const result = await response.json()
    if (result && result.url) window.location.href = result.url
    else window.location.href = unsuccessUrl
    console.log(result)
  } catch {
    window.location.href = unsuccessUrl
  }
}
